'use client';

import { c } from '../../../utils/tailwind-merge';
import { CheckboxV2 } from '../../atoms/Checkbox.v2';
import type { CheckboxWithLabelProps } from './CheckboxWithLabel.types';

const CHECKED_FONT =
  'group-has-[button[data-state=checked]]:font-medium group-has-[button[data-state=indeterminate]]:font-medium';

function getLabelStyles(disabled: boolean, isDark: boolean): string {
  if (disabled) {
    return isDark
      ? 'cursor-not-allowed text-grey-600'
      : 'cursor-not-allowed text-grey-450';
  }
  return isDark
    ? `text-grey-200 ${CHECKED_FONT} group-has-[button[data-state=checked]]:text-white group-has-[button[data-state=indeterminate]]:text-white`
    : `text-[#141414] opacity-90 group-hover:opacity-100 group-has-[button[data-state=checked]]:opacity-100 group-has-[button[data-state=indeterminate]]:opacity-100 ${CHECKED_FONT}`;
}

export function CheckboxWithLabel({
  id,
  checked,
  defaultChecked,
  disabled = false,
  count,
  microcopy,
  children,
  onCheckedChange,
  className,
  size = 'default',
  style,
  isDarkTheme = false,
  role,
  'aria-level': ariaLevel,
  'aria-selected': ariaSelected,
  onClick,
  onKeyDown,
}: CheckboxWithLabelProps): React.JSX.Element {
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- role="treeitem" is passed dynamically
    <div
      className={c(
        'group flex min-h-10 w-full items-center gap-3 rounded-lg transition-colors',
        disabled
          ? 'cursor-not-allowed'
          : isDarkTheme
            ? 'cursor-pointer hover:bg-white/5'
            : 'cursor-pointer hover:bg-grey-100',
        className
      )}
      style={style}
      role={role}
      aria-level={ariaLevel}
      aria-selected={ariaSelected}
      onClick={onClick}
      onKeyDown={onKeyDown}
    >
      <CheckboxV2
        id={id}
        checked={checked}
        defaultChecked={defaultChecked}
        disabled={disabled}
        isDarkTheme={isDarkTheme}
        onCheckedChange={onCheckedChange}
        className={
          !disabled && !isDarkTheme
            ? 'data-[state=unchecked]:group-hover:border-grey-700 data-[state=unchecked]:group-hover:bg-grey-200'
            : undefined
        }
      />
      <label
        htmlFor={id}
        className={c(
          'flex flex-1 cursor-pointer items-center gap-3 tracking-tight',
          size === 'sm' ? 'text-sm leading-5' : 'text-base leading-6',
          getLabelStyles(disabled, isDarkTheme)
        )}
      >
        {children ?? (
          <>
            <span className="flex-1">{microcopy?.label}</span>
            {count !== undefined && (
              <span
                className={c(
                  'rounded px-1 text-xs leading-5 tracking-tight',
                  isDarkTheme
                    ? 'bg-grey-800 text-grey-400'
                    : 'bg-grey-100 text-grey-700'
                )}
              >
                {count.toLocaleString()}
              </span>
            )}
          </>
        )}
      </label>
    </div>
  );
}
